import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import CampaignIcon from '@mui/icons-material/Campaign';
import { rootStore } from '../../../stores/RootStore';
import i18next from '../../../lang';
import { adminStyles } from '../../../theme/adminStyles';

// HTML内のURLを自動的にクリック可能なリンクに変換する
const linkifyHtml = (html: string): string => {
  // 既存の<a>タグ内のURLは変換しないようにする
  const parts = html.split(/(<a\s[^>]*>.*?<\/a>)/gi);
  return parts.map(part => {
    // 既存のアンカータグはそのまま返す
    if (part.match(/^<a\s/i)) return part;
    // HTMLタグの属性内のURLは変換しない
    return part.replace(
      /(^|[^"'=])(https?:\/\/[^\s<>"']+)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#1976d2;text-decoration:underline;">$2</a>'
    );
  }).join('');
};

interface Announcement {
  id: number;
  userId: string;
  title: string;
  content: string;
  isImportant: number;
  isHidden: number;
  createdAt: string;
  updatedAt: string;
}

const AnnouncementDetail = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { authStore, viewStatusStore } = rootStore;
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  const user = authStore.user;

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/announcements/${id}`);
      setAnnouncement(response.data);
    } catch (error) {
      console.error('Failed to fetch announcement:', error);
    }
  };

  const handleDelete = async () => {
    if (!announcement) return;

    try {
      viewStatusStore.setLoadingStatus({ open: true });
      await axios.delete(`/api/v1/admin/announcements/${announcement.id}`);
      navigate('/admin/announcements');
    } catch (error: any) {
      console.error('Failed to delete announcement:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  if (!announcement) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={adminStyles.pageContainer}>
      {/* ヘッダー */}
      <Box sx={adminStyles.headerBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate((location.state as any)?.from || '/admin/announcements')}
            sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        {user?.level === 'ADMIN' && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/announcements/edit/${announcement.id}`)}
              sx={adminStyles.actionButton}
            >
              {i18next.t('common.edit')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={adminStyles.actionButton}
            >
              {i18next.t('common.delete')}
            </Button>
          </Stack>
        )}
      </Box>

      {/* 공지사항 내용 */}
      <Paper sx={adminStyles.contentPaper}>
        {/* 제목 및 메타 정보 */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 }, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start'}}>
              {announcement.isImportant === 1 ? (
                <NotificationImportantIcon sx={{ color: '#dc3545' }} />
              ) : (
                <CampaignIcon sx={{ color: '#6c757d' }} />
              )}
              {announcement.isImportant === 1 && (
                <Chip
                  label={i18next.t('common.important')}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              )}
              {announcement.isHidden === 1 && (
                <Chip
                  label={i18next.t('invoice.hidden')}
                  color="default"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 2 }, color: '#6c757d', fontSize: '0.875rem' }}>
              <Typography variant="body2">
                {i18next.t('common.created_at')}: {formatDate(announcement.createdAt)}
              </Typography>
              {announcement.updatedAt !== announcement.createdAt && (
                  <Typography variant="body2">
                    {i18next.t('common.updated_at')}: {formatDate(announcement.updatedAt)}
                  </Typography>
              )}
            </Box>
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: announcement.isImportant ? 600 : 500,
              color: announcement.isImportant ? '#dc3545' : '#212529',
              mb: 2
            }}
          >
            {announcement.title}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 내용 */}
        <Box>
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              '& p': {
                margin: 0
              }
            }}
            dangerouslySetInnerHTML={{ __html: linkifyHtml(announcement.content) }}
          />
        </Box>
      </Paper>
    </Container>
  );
});

export default AnnouncementDetail; 